import type { FormError } from '../../../utils/volunteer-utils.ts';
import {
  AggregateConflictError,
  DuplicateInShiftError,
  EventFullError,
  GuestUnresolvedError,
  SameDayConflictError,
  SessionExpiredError,
  ShiftFullError,
  SlotConflictError,
} from './volunteer-conflict-errors.ts';

const fullShiftFormError: FormError = {
  type: 'full-shift',
  key: 'volunteers:registration.shiftFullError',
};

/** The three message variants a remediable conflict (duplicate / same-day) maps to. */
interface ConflictVariants {
  /** Named but couldn't auto-remove — ask the user to double-check. */
  doubleCheck: { type: FormError['type']; key: string };
  /** Removed for the user — ask them to review the adjusted selection. */
  review: { type: FormError['type']; key: string };
  /** Nothing mapped — generic prompt. */
  generic: FormError;
}

/**
 * Map a remediable conflict (seat removed + shift locked) to its form error.
 * Shared by duplicate and same-day, which differ only in copy: unresolved →
 * double-check, removed → review, else → generic.
 */
const conflictFormErrors = (
  error: { unresolvedContactNames: string[]; removedContactNames: string[] },
  variants: ConflictVariants,
): FormError[] => {
  if (error.unresolvedContactNames.length > 0) {
    return [
      {
        type: variants.doubleCheck.type,
        key: variants.doubleCheck.key,
        params: {
          names: error.unresolvedContactNames.join(', '),
          count: error.unresolvedContactNames.length,
        },
      },
    ];
  }
  if (error.removedContactNames.length > 0) {
    return [
      {
        type: variants.review.type,
        key: variants.review.key,
        params: {
          names: error.removedContactNames.join(', '),
          count: error.removedContactNames.length,
        },
      },
    ];
  }
  return [variants.generic];
};

/** Duplicate response → one or more errors (may add a full-shift line). */
const duplicateFormErrors = (error: DuplicateInShiftError): FormError[] => {
  const errors = conflictFormErrors(error, {
    doubleCheck: {
      type: 'duplicate-double-check',
      key: 'volunteers:registration.duplicateDoubleCheckError',
    },
    review: {
      type: 'duplicate-review',
      key: 'volunteers:registration.duplicateRemovedReviewError',
    },
    generic: {
      type: 'duplicate-generic',
      key: 'volunteers:registration.duplicateInShiftError',
    },
  });

  // A shift also filled up this submit; surface it instead of swallowing it.
  if (error.hadFullShiftRemoval) {
    errors.push(fullShiftFormError);
  }

  return errors;
};

/**
 * Same-day response → review or double-check error. Mirrors the duplicate
 * flow (seat removed + shift locked) but with same-day-specific copy.
 */
const sameDayFormErrors = (error: SameDayConflictError): FormError[] =>
  conflictFormErrors(error, {
    doubleCheck: {
      type: 'same-day-double-check',
      key: 'volunteers:registration.sameDayDoubleCheckError',
    },
    review: {
      type: 'same-day-review',
      key: 'volunteers:registration.sameDayRemovedReviewError',
    },
    generic: {
      type: 'same-day-conflict',
      key: 'volunteers:registration.sameDayConflictError',
    },
  });

/**
 * Whether a submit error should bounce the user back to the shifts panel to
 * fix a selection. Session/slot-conflict stay in place (retryable in context).
 */
export const shouldBounceToShifts = (error: unknown): boolean =>
  error instanceof DuplicateInShiftError ||
  error instanceof ShiftFullError ||
  error instanceof SameDayConflictError ||
  error instanceof GuestUnresolvedError ||
  (error instanceof AggregateConflictError &&
    error.errors.some(shouldBounceToShifts));

/** Map a submit error to the untranslated form errors the UI should show. */
export const mapSubmitErrorToFormErrors = (error: unknown): FormError[] => {
  // A mixed response: flatten each child into its own form error(s).
  if (error instanceof AggregateConflictError) {
    return error.errors.flatMap(mapSubmitErrorToFormErrors);
  }
  if (error instanceof SessionExpiredError) {
    return [
      {
        type: 'session-expired',
        key: 'volunteers:registration.sessionExpiredError',
      },
    ];
  }
  if (error instanceof SlotConflictError) {
    return [
      {
        type: 'slot-conflict',
        key: 'volunteers:registration.slotConflictError',
      },
    ];
  }
  if (error instanceof DuplicateInShiftError) {
    return duplicateFormErrors(error);
  }
  if (error instanceof ShiftFullError) {
    return [fullShiftFormError];
  }
  if (error instanceof EventFullError) {
    return [
      { type: 'event-full', key: 'volunteers:registration.eventFullError' },
    ];
  }
  if (error instanceof SameDayConflictError) {
    return sameDayFormErrors(error);
  }
  if (error instanceof GuestUnresolvedError) {
    return [
      {
        type: 'guest-unresolved',
        key: 'volunteers:registration.guestUnresolvedError',
      },
    ];
  }
  return [{ type: 'submit', key: 'volunteers:registration.submitError' }];
};
